import { ROUTES } from "@/routes/routes";

/** postMessage source for the study pane iframe. Same origin only. */
export const STUDY_EMBED_SOURCE = "fairgen-study";

export const STUDY_EMBED_PARAM = "embed";
export const STUDY_EMBED_VALUE = "thread";

export interface StudyEmbedDraftMessage {
  source: typeof STUDY_EMBED_SOURCE;
  type: "draft";
  draftToken: string;
}

export interface StudyEmbedLaunchedMessage {
  source: typeof STUDY_EMBED_SOURCE;
  type: "launched";
  studyToken: string;
}

export interface StudyEmbedOpenTopMessage {
  source: typeof STUDY_EMBED_SOURCE;
  type: "open-top";
  href: string;
}

export interface StudyEmbedCloseMessage {
  source: typeof STUDY_EMBED_SOURCE;
  type: "close-ok" | "close-blocked";
}

/** Visible question-list rectangle in the child viewport, after scroll clipping. */
export interface StudyQuestionsViewport {
  top: number;
  left: number;
  right: number;
  bottom: number;
  viewportWidth: number;
  viewportHeight: number;
}

export function studyQuestionsVisibleInParent(
  value: unknown,
  frame: {top: number; left: number; right: number; bottom: number; width: number; height: number},
  parentWidth: number,
  parentHeight: number,
  nativeClip: {top: number; left: number; right: number; bottom: number} | null,
): value is StudyQuestionsViewport {
  if (!value || typeof value !== "object" || !nativeClip || ![nativeClip.top, nativeClip.left, nativeClip.right, nativeClip.bottom].every(Number.isFinite)) return false;
  const r = value as StudyQuestionsViewport;
  if (![r.top, r.left, r.right, r.bottom, r.viewportWidth, r.viewportHeight].every((n) => typeof n === "number" && Number.isFinite(n))) return false;
  if (r.viewportWidth <= 0 || r.viewportHeight <= 0 || r.left < 0 || r.top < 0 || r.right > r.viewportWidth || r.bottom > r.viewportHeight || r.right <= r.left || r.bottom <= r.top) return false;
  const sx = frame.width / r.viewportWidth;
  const sy = frame.height / r.viewportHeight;
  return Math.min(frame.left + r.right * sx, frame.right, parentWidth, nativeClip.right) > Math.max(frame.left + r.left * sx, frame.left, 0, nativeClip.left)
    && Math.min(frame.top + r.bottom * sy, frame.bottom, parentHeight, nativeClip.bottom) > Math.max(frame.top + r.top * sy, frame.top, 0, nativeClip.top);
}

export interface StudyEmbedQuickReviewMessage {
  source: typeof STUDY_EMBED_SOURCE;
  type: "quick-review-requested" | "quick-questions-displayed";
  questionsViewport?: StudyQuestionsViewport | null;
  draftToken: string;
  revision: number;
}

export type StudyEmbedMessage =
  | StudyEmbedQuickReviewMessage
  | StudyEmbedDraftMessage
  | StudyEmbedLaunchedMessage
  | StudyEmbedOpenTopMessage
  | StudyEmbedCloseMessage;

export interface EmbedLocation {
  pathname: string;
  search: string;
}

export interface EmbedNavigationPlan {
  action: "navigate" | "open-top";
  href: string;
}

export function isThreadEmbedSearch(search: string): boolean {
  const query = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(query).get(STUDY_EMBED_PARAM) === STUDY_EMBED_VALUE;
}

export function isThreadEmbedLocation(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return isThreadEmbedSearch(window.location.search);
}

/**
 * Creator (`/tests/new`) and one study's results (`/tests/:id/results`).
 * The shape comes from ROUTES so a path change stays in one place.
 */
export function isStudyEmbedSurfacePath(pathname: string): boolean {
  const normalized = stripTrailingSlash(pathname);
  if (normalized === ROUTES.testsNew) {
    return true;
  }
  const sampleId = "study";
  const sample = ROUTES.studyResults(sampleId);
  const token = `/${sampleId}/`;
  const tokenAt = sample.indexOf(token);
  if (tokenAt < 0) {
    return false;
  }
  const prefix = sample.slice(0, tokenAt + 1);
  const suffix = sample.slice(tokenAt + token.length - 1);
  if (!normalized.startsWith(prefix) || !normalized.endsWith(suffix)) {
    return false;
  }
  const id = normalized.slice(prefix.length, normalized.length - suffix.length);
  if (id.length === 0 || id.includes("/")) {
    return false;
  }
  return true;
}

export function planEmbedNavigation(
  href: string,
  current: EmbedLocation,
): EmbedNavigationPlan {
  if (!isThreadEmbedSearch(current.search)) {
    return { action: "navigate", href };
  }
  let url: URL;
  try {
    url = new URL(href, `${window.location.origin}${current.pathname}${current.search}`);
  } catch {
    return { action: "open-top", href };
  }
  if (url.origin !== window.location.origin) {
    return { action: "open-top", href };
  }
  const nextPath = `${url.pathname}${url.search}${url.hash}`;
  if (!isStudyEmbedSurfacePath(url.pathname)) {
    return { action: "open-top", href: nextPath };
  }
  url.searchParams.set(STUDY_EMBED_PARAM, STUDY_EMBED_VALUE);
  return {
    action: "navigate",
    href: `${url.pathname}${url.search}${url.hash}`,
  };
}

export function postStudyEmbedMessage(message: StudyEmbedMessage): void {
  if (!isThreadEmbedLocation() || typeof window === "undefined") {
    return;
  }
  const parentWindow = window.parent;
  if (!parentWindow) {
    return;
  }
  parentWindow.postMessage(message, window.location.origin);
}

/**
 * One draft notice per token for this document. StrictMode remounts the
 * creator, and the parent only needs the first time the id appears.
 */
const postedDraftTokens = new Set<string>();

export function postStudyEmbedDraft(draftToken: string): void {
  if (!isThreadEmbedLocation() || postedDraftTokens.has(draftToken)) {
    return;
  }
  postedDraftTokens.add(draftToken);
  postStudyEmbedMessage({
    source: STUDY_EMBED_SOURCE,
    type: "draft",
    draftToken,
  });
}

/**
 * Builder publishes true while it has unsaved work, and clears on unmount.
 * Null means the builder is not mounted (review, results): close is allowed.
 */
let embedUnsavedWork: boolean | null = null;

/** Test isolation. The set lives for the document, which a test file reuses. */
export function resetStudyEmbedBridgeForTests(): void {
  postedDraftTokens.clear();
  embedUnsavedWork = null;
}

export function publishEmbedUnsavedWork(hasUnsavedWork: boolean): void {
  embedUnsavedWork = hasUnsavedWork;
}

export function clearEmbedUnsavedWork(): void {
  embedUnsavedWork = null;
}

export function embedCloseAnswer(): "close-ok" | "close-blocked" {
  if (embedUnsavedWork === true) {
    return "close-blocked";
  }
  return "close-ok";
}

function stripTrailingSlash(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}
